import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hasValue } from '@progress-fe/core';
import { observer } from 'mobx-react-lite';
import { Tree as ReactTree, ControlledTreeEnvironment } from 'react-complex-tree';
import { TreeNode } from './components';
import 'react-complex-tree/lib/style-modern.css';
export const TREE_ROOT_ITEM_ID = 'ROOT';
const TreeFC = ({ isCollapsed, treeId, treeIcon, treeItems, scrollRef, wasScrolled, activeItems = [], isAlwaysExpanded, isOnlyMainExpandable, isClickOnActiveDisabled, onUpdateCollapsed, onUpdateWasScrolled, onClickTreeItem }) => {
    const [focusedItem, setFocusedItem] = useState(undefined);
    const [expandedItems, setExpandedItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const wasInitialized = useRef(false);
    const mainNodeId = useMemo(() => {
        return treeItems[TREE_ROOT_ITEM_ID]?.children?.[0] ?? '';
    }, [treeItems]);
    useEffect(() => {
        if (!wasInitialized.current && Object.keys(treeItems).length > 0) {
            setExpandedItems(Object.keys(treeItems));
            wasInitialized.current = true;
        }
    }, [treeItems]);
    useEffect(() => {
        if (wasInitialized.current && hasValue(isCollapsed)) {
            setExpandedItems((prev) => isCollapsed ? prev.filter((i) => i !== mainNodeId) : [...prev, mainNodeId]);
        }
    }, [isCollapsed, mainNodeId]);
    const canExpandOrCollapse = useCallback((index) => {
        if (isAlwaysExpanded) {
            return false;
        }
        else if (isOnlyMainExpandable && index !== mainNodeId) {
            return false;
        }
        return true;
    }, [isAlwaysExpanded, isOnlyMainExpandable, mainNodeId]);
    const handleExpandOrCollapse = useCallback((idx, newIsCollapsed) => {
        if (canExpandOrCollapse(idx)) {
            if (newIsCollapsed) {
                setExpandedItems((prev) => prev.filter((i) => i !== idx));
            }
            else {
                setExpandedItems((prev) => [...prev, idx]);
            }
            if (idx === mainNodeId) {
                onUpdateCollapsed?.(newIsCollapsed);
            }
        }
    }, [canExpandOrCollapse, mainNodeId, onUpdateCollapsed]);
    return (_jsx(ControlledTreeEnvironment, { items: treeItems, getItemTitle: (item) => item.data.name ?? '', viewState: {
            [treeId]: {
                focusedItem,
                selectedItems,
                expandedItems
            }
        }, renderTreeContainer: ({ children, containerProps }) => (_jsx("div", { ...containerProps, style: { padding: '0', background: 'white' }, children: children })), renderItemsContainer: ({ children, containerProps }) => (_jsx("ul", { ...containerProps, style: { width: '100%' }, children: children })), renderItem: (props) => (_jsx(TreeNode, { ...props, treeIcon: treeIcon, scrollRef: scrollRef, activeItems: activeItems, mainNodeId: mainNodeId, wasScrolled: wasScrolled, isAlwaysExpanded: isAlwaysExpanded, isOnlyMainExpandable: isOnlyMainExpandable, isClickOnActiveDisabled: isClickOnActiveDisabled, onUpdateWasScrolled: onUpdateWasScrolled, onClickTreeItem: onClickTreeItem })), onFocusItem: (item) => setFocusedItem(item.index), onSelectItems: (items) => setSelectedItems(items), onExpandItem: (item) => handleExpandOrCollapse(item.index, false), onCollapseItem: (item) => handleExpandOrCollapse(item.index, true), children: _jsx(ReactTree, { treeId: treeId, rootItem: TREE_ROOT_ITEM_ID }) }));
};
export const Tree = observer(TreeFC);
//# sourceMappingURL=Tree.js.map